var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var WeightedAverageConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { buildInputsMap, validateSourceWithDetail } from './validation/validateHelper';
import { weightedAverageConfigSchemaValidation } from '../validations.generated';
export const weightedAverageConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:WeightedAverageConfig',
    type: 'object',
    properties: {
        columnName: { type: 'string', minLength: 1 },
        valueColumn: { type: 'string', minLength: 1 },
        weightColumn: { type: 'string', minLength: 1 },
        groupByColumns: {
            type: 'array',
            items: { type: 'string' },
            uniqueItems: true
        }
    },
    required: ['columnName', 'valueColumn', 'weightColumn', 'groupByColumns']
};
let WeightedAverageConfig = WeightedAverageConfig_1 = class WeightedAverageConfig extends ToolConfig {
    constructor(columnName, valueColumn, weightColumn, groupByColumns, customFormats) {
        super(ToolType.WEIGHTED_AVERAGE, customFormats);
        this.columnName = columnName;
        this.valueColumn = valueColumn;
        this.weightColumn = weightColumn;
        this.groupByColumns = groupByColumns;
        JSON.validate(this, WeightedAverageConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return WeightedAverageConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return WeightedAverageConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return WeightedAverageConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return WeightedAverageConfig_1.outs;
    }
    static get in() {
        return WeightedAverageConfig_1.ins[0];
    }
    static get out() {
        return WeightedAverageConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.WEIGHTED_AVERAGE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['weighted mean'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            // output schema will be the group by columns + weighted average column
            const exp = tsb.export(this.out);
            const dependents = [];
            const srcValueColumn = exp.findSourceColumn(this.in, this.valueColumn);
            dependents.push(srcValueColumn);
            dependents.push(exp.findSourceColumn(this.in, this.weightColumn));
            dependents.push(...this.getGroupByDependents(exp, this.in, this.groupByColumns, dependents));
            exp.makeNew(new Column(this.columnName, 'number'), dependents, true);
            const newColumnFormats = new Map();
            if (srcValueColumn.customFormat) {
                newColumnFormats.set(this.columnName, srcValueColumn.customFormat.copyWithAutoGeneratedTrue());
            }
            exp.initializeColumnFormats().setColumnFormats(newColumnFormats).setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const inputs = buildInputsMap(this.groupByColumns, []);
        inputs.set(this.valueColumn, ['number']);
        inputs.set(this.weightColumn, ['number']);
        const sourceValidationResult = validateSourceWithDetail(source, inputs, []);
        if (!sourceValidationResult.isValid) {
            return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
        }
        const groupByColumns = new Set(this.groupByColumns);
        if (groupByColumns.has(this.columnName)) {
            return ToolValidationResult.getBadMetadataResult(`Column name '${this.columnName}' is already in group by columns.`, `Rename the new column name to something else.`);
        }
        return ToolValidationResult.getValidResult();
    }
};
WeightedAverageConfig = WeightedAverageConfig_1 = __decorate([
    JsonSchemaV2(weightedAverageConfigSchema, weightedAverageConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.WEIGHTED_AVERAGE)
], WeightedAverageConfig);
export { WeightedAverageConfig };
//# sourceMappingURL=weightedAverageConfig.js.map